/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {GenericObject} from '@ion/core/src/types/entities';

// a record is keyed by its id, or by the record itself when it has none, so
// id-less records are never merged into one another ...
const getQueueKey = (item: GenericObject, idField: string): unknown => item?.[idField] ?? item;

/**
 * Returns the records not yet sent to a client-side layer (fleet assets,
 * geofences). Records are tracked by object: as pages accumulate, the
 * earlier pages' objects are passed again unchanged and are skipped, while
 * any new object (a refetch or a hydrate builds new ones) is returned, so
 * nothing changed is ever left unsent.
 *
 * ```typescript
 *
 *   import {getUnappliedItems} from '@ion/dls/src/maps/helpers/layerEditQueue';
 *   ...
 *
 *   const changed = getUnappliedItems(assets, appliedAssetsRef.current);
 *
 * ```
 *
 * @param   items   The records the layer should show.
 * @param   applied The record objects already sent to the layer.
 * @returns         The records that still need to be sent.
 */
export const getUnappliedItems = <T extends object>(items: T[], applied: WeakSet<object>): T[] => (
  items.filter(function(item) {
    return !applied.has(item);
  })
);

/**
 * Adds incoming records to a layer's pending edits, keeping one entry per id
 * with the latest record winning. A debounced flush then applies every
 * queued record, instead of only the last call's records, and never adds the
 * same record twice.
 *
 * ```typescript
 *
 *   import {queueLayerItems} from '@ion/dls/src/maps/helpers/layerEditQueue';
 *   ...
 *
 *   pendingRef.current = queueLayerItems(pendingRef.current, geofences, 'geofenceId');
 *
 * ```
 *
 * @param   pending  The records already waiting to be applied.
 * @param   incoming The records to add.
 * @param   idField  The field identifying a record; `assetId` by default.
 * @returns          The pending records, one per id.
 */
export const queueLayerItems = <T extends object>(pending: T[], incoming: T[], idField = 'assetId'): T[] => (
  Array.from([...pending, ...(incoming ?? [])].reduce(function(queue, item) {
    queue.set(getQueueKey(item, idField), item);
    return queue;
  }, new Map<unknown, T>()).values())
);
